import { Upload, X, Plus, Save, Trash2, ChevronDown, } from "lucide-react";
import { Users,  Play, Eye, EyeOff, Globe, Eraser } from 'lucide-react';
import { BeatLoader, FadeLoader } from 'react-spinners';
import { Search, User, ChevronUp, Edit3, Filter, RefreshCcw } from 'lucide-react';

import { useState, useRef, useEffect } from "react";
import transitionGraphAPI from '../../../../../services/transitionGraphAPI';
import { getUserSession } from "../../../../../utils/auth";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useSelector } from 'react-redux';
import imageService from '../../../../../services/imageHandlerAPI';

// Import the CSS file
import './TransitionGraph.css';

// Zone colors configuration
const availableColors = ["#3b82f6", "#10b981", "#8b5cf6", "#f59e0b", "#ef4444"];
const defaultColor = "#6b7280";

const TransitionGraph = (
  { transitions = {}, zoneMetadata = {}, hw_id=""}
) => {

  // Tab management state
  const [tabs, setTabs] = useState([]);
  const [activeTabId, setActiveTabId] = useState(null);
  const [showTabDropdown, setShowTabDropdown] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // 'success' | 'error' | null
  const [showContextPanel, setShowContextPanel] = useState(true);
  const [showLoadGraphs, setShowLoadGraphs] = useState(false);

  // Cloud save state
  const [isCloudSaving, setIsCloudSaving] = useState(false);
  const [cloudSaveStatus, setCloudSaveStatus] = useState(null);
  const [selectedNodes, setSelectedNodes] = useState([]);

  const handleNewBlank = () => {
    setActiveTabId(null);
    setIsCollapsed(true);
    createNewTab();
  };

  const handleBackToBrowse = () => {
    setActiveTabId(null);
    setSelectedNodes([]);
  };

  const navigate = useNavigate();

  // Graph state (per tab)
  const [hoveredZone, setHoveredZone] = useState(null);
  const [draggedZone, setDraggedZone] = useState(null);
  const [draggedEdgeEnd, setDraggedEdgeEnd] = useState(null);
  const [draggedEdge, setDraggedEdge] = useState(null);
  const [isDraggingFromSidebar, setIsDraggingFromSidebar] = useState(false);
  const [mousePosition, setMousePosition] = useState(null);
  const [showImageUpload, setShowImageUpload] = useState(false);
  const [tabDropdownRef, setTabDropdownRef] = useState(null);
  const svgRef = useRef(null);
  const fileInputRef = useRef(null);
  const modalRef = useRef(null);

  const [isCollapsed, setIsCollapsed] = useState(false);
  const handlePathSelect = (path) => {
    setActiveTabId(path.id);
    const tab = tabs.find(tab => tab.id === path.id);
    if (tab) {
      setSelectedNodes(Object.keys(tab.data.droppedZones || {}));
    }
  };

  // Constants
  const FIXED_STROKE_WIDTH = 6;
  const FIXED_ARROW_SIZE = 8;
  const FIXED_EDGE_OPACITY = 1;
  const SVG_WIDTH = 1000;
  const SVG_HEIGHT = 700;
  const NODE_RADIUS = 35;
  const MIN_DISTANCE = NODE_RADIUS * 3;

  // Helper functions to work with zone IDs and names
  const getZoneId = (zoneName) => {
    return zoneMetadata.zoneNameToId?.[zoneName] || zoneName;
  };

  const getZoneName = (zoneId) => {
    return zoneMetadata.zoneIdToName?.[zoneId] || zoneId;
  };

  const getZoneDisplayName = (zoneIdentifier) => {
    // If it looks like an ID (UUID format), get the name
    if (typeof zoneIdentifier === 'string' && zoneIdentifier.includes('-') && zoneIdentifier.length > 20) {
      return getZoneName(zoneIdentifier) || zoneIdentifier;
    }
    // Otherwise assume it's already a name
    return zoneIdentifier;
  };

  // Storage keys
  const STORAGE_KEY_TABS = "transition-graph-tabs-v2"; // Updated version for zone ID support
  const STORAGE_KEY_ACTIVE_TAB = "transition-graph-active-tab-v2";

  // Build data structures - use zone metadata if available
  const allZones = new Set();
  const edges = [];

  // Use zone metadata if available, otherwise fall back to transition data
  if (zoneMetadata.zones && zoneMetadata.zones.length > 0) {
    zoneMetadata.zones.forEach(zone => {
      allZones.add(zone.id); // Use zone ID as primary identifier
    });
  }

  Object.entries(transitions).forEach(([fromZone, toZones]) => {
    const fromZoneId = getZoneId(fromZone);
    allZones.add(fromZoneId);
    
    Object.entries(toZones).forEach(([toZone, probability]) => {
      const toZoneId = getZoneId(toZone);
      allZones.add(toZoneId);
      edges.push({ 
        from: fromZoneId, 
        to: toZoneId, 
        probability,
        fromName: fromZone,
        toName: toZone
      });
    });
  });

  const zones = Array.from(allZones);

  // Normalization functions (updated for zone IDs)
  const normalizePosition = (pos) => {
    const clampedX = Math.max(0, Math.min(pos.x, SVG_WIDTH));
    const clampedY = Math.max(0, Math.min(pos.y, SVG_HEIGHT));
    
    return {
      x: Math.round((clampedX / SVG_WIDTH) * 10000) / 10000,
      y: Math.round((clampedY / SVG_HEIGHT) * 10000) / 10000
    };
  };

  const denormalizePosition = (pos) => {
    const clampedX = Math.max(0, Math.min(pos.x, 1));
    const clampedY = Math.max(0, Math.min(pos.y, 1));
    
    return {
      x: clampedX * SVG_WIDTH,
      y: clampedY * SVG_HEIGHT
    }; 
  };

  const normalizeNumber = (num) => {
    return Math.round(num * 10000) / 10000;
  };

  const normalizeTabData = (data) => {
    const normalized = {
      droppedZones: {},
      nodePositions: {},
      edgeEndpoints: {},
      edgeCurvatures: {},
      backgroundImageUrl: data.backgroundImageUrl, // This will be the signed URL for display
      hasBackgroundImage: data.hasBackgroundImage || false, // Simple boolean flag
    };

    // ... rest of normalization logic stays the same
    Object.entries(data.droppedZones || {}).forEach(([zoneIdentifier, pos]) => {
      const zoneId = getZoneId(zoneIdentifier);
      normalized.droppedZones[zoneId] = normalizePosition(pos);
    });

    Object.entries(data.nodePositions || {}).forEach(([zoneIdentifier, pos]) => {
      const zoneId = getZoneId(zoneIdentifier);
      normalized.nodePositions[zoneId] = normalizePosition(pos);
    });

    Object.entries(data.edgeEndpoints || {}).forEach(([edgeKey, endpoints]) => {
      let fromZone, toZone;
      
      if (edgeKey.includes('|')) {
        [fromZone, toZone] = edgeKey.split('|');
      } else {
        const lastDashIndex = edgeKey.lastIndexOf('-');
        if (lastDashIndex === -1) return;
        fromZone = edgeKey.substring(0, lastDashIndex);
        toZone = edgeKey.substring(lastDashIndex + 1);
      }
      
      const fromZoneId = getZoneId(fromZone);
      const toZoneId = getZoneId(toZone);
      const normalizedKey = `${fromZoneId}|${toZoneId}`;
      
      normalized.edgeEndpoints[normalizedKey] = {};
      if (endpoints.start) {
        normalized.edgeEndpoints[normalizedKey].start = normalizePosition(endpoints.start);
      }
      if (endpoints.end) {
        normalized.edgeEndpoints[normalizedKey].end = normalizePosition(endpoints.end);
      }
    });

    Object.entries(data.edgeCurvatures || {}).forEach(([edgeKey, curvature]) => {
      let fromZone, toZone;
      
      if (edgeKey.includes('|')) {
        [fromZone, toZone] = edgeKey.split('|');
      } else {
        const lastDashIndex = edgeKey.lastIndexOf('-');
        if (lastDashIndex === -1) return;
        fromZone = edgeKey.substring(0, lastDashIndex);
        toZone = edgeKey.substring(lastDashIndex + 1);
      }
      
      const fromZoneId = getZoneId(fromZone);
      const toZoneId = getZoneId(toZone);
      const normalizedKey = `${fromZoneId}|${toZoneId}`;
      normalized.edgeCurvatures[normalizedKey] = normalizeNumber(curvature);
    });

    return normalized;
  };

  // Simplified denormalization
  const denormalizeTabData = (data) => {
    const denormalized = {
      droppedZones: {},
      nodePositions: {},
      edgeEndpoints: {},
      edgeCurvatures: {},
      backgroundImageUrl: data.backgroundImageUrl,
      hasBackgroundImage: data.hasBackgroundImage || false,
    };

    // ... rest of denormalization logic stays the same
    Object.entries(data.droppedZones || {}).forEach(([zoneId, pos]) => {
      denormalized.droppedZones[zoneId] = denormalizePosition(pos);
    });

    Object.entries(data.nodePositions || {}).forEach(([zoneId, pos]) => {
      denormalized.nodePositions[zoneId] = denormalizePosition(pos);
    });

    Object.entries(data.edgeEndpoints || {}).forEach(([edgeKey, endpoints]) => {
      denormalized.edgeEndpoints[edgeKey] = {};
      if (endpoints.start) {
        denormalized.edgeEndpoints[edgeKey].start = denormalizePosition(endpoints.start);
      }
      if (endpoints.end) {
        denormalized.edgeEndpoints[edgeKey].end = denormalizePosition(endpoints.end);
      }
    });

    Object.entries(data.edgeCurvatures || {}).forEach(([edgeKey, curvature]) => {
      denormalized.edgeCurvatures[edgeKey] = normalizeNumber(curvature);
    });

    return denormalized;
  };

  // Get current active tab data
  const getCurrentTab = () => {
    return tabs.find(tab => tab.id === activeTabId);
  };

  const getCurrentTabData = () => {
    const tab = getCurrentTab();
    return tab ? tab.data : {
      droppedZones: {},
      nodePositions: {},
      edgeEndpoints: {},
      edgeCurvatures: {},
      backgroundImage: null,
    };
  };

  // Update current tab data with proper state management and normalization
  const updateCurrentTabData = (updates) => {
    if (!activeTabId) return;
    
    setTabs(prevTabs => {
      const newTabs = prevTabs.map(tab => 
        tab.id === activeTabId 
          ? { ...tab, data: { ...tab.data, ...updates } }
          : tab
      );
      
      // Save normalized data to localStorage immediately
      try {
        const normalizedTabs = newTabs.map(tab => ({
          ...tab,
          data: normalizeTabData(tab.data)
        }));
        // localStorage.setItem(STORAGE_KEY_TABS, JSON.stringify(normalizedTabs));
      } catch (error) {
        console.error("Error saving tabs to localStorage:", error);
      }

      // console.log("Updated Current Tab Data:", newTabs.find(tab => tab.id === activeTabId));
      
      return newTabs;
    });
  };

  // Save current state to localStorage with normalization
  const saveToLocalStorage = () => {
    setIsSaving(true);
    try {
      const normalizedTabs = tabs.map(tab => ({
        ...tab,
        data: normalizeTabData(tab.data)
      }));
      // localStorage.setItem(STORAGE_KEY_TABS, JSON.stringify(normalizedTabs));
      // localStorage.setItem(STORAGE_KEY_ACTIVE_TAB, activeTabId || "");
      
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(null), 2000);
    } catch (error) {
      console.error("Error saving to localStorage:", error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  const fetchUserResponse = useSelector(state => state.fetchUserResponse);
  const businesses = fetchUserResponse?.businesses;
  let business_name = businesses?.map(business => Object.keys(business))[0];


  // Simplified save to cloud function
  const saveToCloud = async (newTab) => {
    let currentTab = null;
    if (!newTab) {
      currentTab = getCurrentTab();
      if (!currentTab) return;
    } else {
      currentTab = newTab;
    }

    setIsCloudSaving(true);
    setCloudSaveStatus(null);

    try {
      let idToken = await getUserSession();
      if (!idToken) {
        toast.error('No authentication token found. Please sign in again.');
        setTimeout(() => {
          navigate('/auth/signin');
        }, 1000);
        return;
      }

      const userInfo = {
        user_email: localStorage.getItem('email'),
        business_name: business_name,
        hw_id: hw_id
      };

      const normalizedData = normalizeTabData(currentTab.data);
      
      const graphData = {
        id: currentTab.cloudId || undefined,
        name: currentTab.name,
        description: currentTab.description || '',
        // Save just a boolean flag - the path ID IS the image key
        backgroundImageUrl: normalizedData.hasBackgroundImage ? activeTabId : null,
        droppedZones: normalizedData.droppedZones,
        edgeEndpoints: normalizedData.edgeEndpoints,
        edgeCurvatures: normalizedData.edgeCurvatures
      };

      const result = await transitionGraphAPI.saveGraph({...userInfo, ...graphData }, idToken);

      if (result.success) {
        setTabs(prevTabs => 
          prevTabs.map(tab => 
            tab.id === activeTabId 
              ? { ...tab, id:result.data.graphId, cloudId: result.data.graphId, lastSaved: new Date() }
              : tab
          )
        );
        setActiveTabId(result.data.graphId);
        setCloudSaveStatus('success');
        setTimeout(() => setCloudSaveStatus(null), 3000);
        toast.success('Graph saved to cloud successfully');
      }

    } catch (error) {
      console.error('Error saving to cloud:', error);
      setCloudSaveStatus('error');
      setTimeout(() => setCloudSaveStatus(null), 5000);
      toast.error('Error saving to cloud: ' + error.message);
    } finally {
      setIsCloudSaving(false);
    }
  };

  // Add new function to handle local draft deletion
  const deleteLocalDraft = () => {
    if (!activeTabId) return;
    
    try {
      // Remove from tabs array
      const newTabs = tabs.filter(tab => tab.id !== activeTabId);
      setTabs(newTabs);
      
      // Clear the active tab ID since we're deleting it
      setActiveTabId(null);
      
      // Update localStorage
      const normalizedTabs = newTabs.map(tab => ({
        ...tab,
        data: normalizeTabData(tab.data)
      }));
      // localStorage.setItem(STORAGE_KEY_TABS, JSON.stringify(normalizedTabs));
      // localStorage.setItem(STORAGE_KEY_ACTIVE_TAB, ""); // Clear active tab
      
      toast.success('Draft deleted successfully');
      setIsCollapsed(false);

    } catch (error) {
      console.error('Error deleting local draft:', error);
      toast.error('Error deleting draft');
    }
  };

  // Update deleteGraphFromCloud to handle the case better
  const deleteGraphFromCloud = async (cloudId) => {
    if (!cloudId) {
      toast.error('Cannot delete: Graph not saved to cloud');
      return;
    }
    
    try {
      let idToken = await getUserSession();
      if (!idToken) {
        toast.error('No authentication token found. Please sign in again.');
        setTimeout(() => {
          navigate('/auth/signin');
        }, 1000);
        return;
      }

      const userInfo = {
        user_email: localStorage.getItem('email'),
        business_name: business_name,
        hw_id: hw_id
      };

      // delete the image saved in S3 if it exists
      if (getCurrentTabData().hasBackgroundImage) {
        try {
          await imageService.deleteImageByPathId(cloudId);
        } catch (error) {
          console.error('Error deleting background image:', error);
          toast.error('Error deleting background image: ' + error.message);
        }
      }
      
      const result = await transitionGraphAPI.deleteGraph({graphId: cloudId, ...userInfo}, idToken);
      
      if (result.success) {
        toast.success('Graph deleted successfully');

        // Remove from local tabs as well
        const newTabs = tabs.filter(tab => tab.id !== activeTabId);
        setTabs(newTabs);
        
        // Update localStorage
        const normalizedTabs = newTabs.map(tab => ({
          ...tab,
          data: normalizeTabData(tab.data)
        }));
        // localStorage.setItem(STORAGE_KEY_TABS, JSON.stringify(normalizedTabs));
        
        handleBackToBrowse();
        setIsCollapsed(false);
      }

    } catch (error) {
      console.error('Error deleting graph:', error);
      toast.error('Error deleting graph: ' + error.message);
    }
  };

  // Check if two positions overlap
  const checkOverlap = (pos1, pos2) => {
    const dx = pos1.x - pos2.x;
    const dy = pos1.y - pos2.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    return distance < MIN_DISTANCE;
  };

  // Generate non-overlapping positions for zones (using zone IDs)
  const generateNonOverlappingPositions = (zoneList) => {
    const positions = {};
    const maxAttempts = 100;
    const margin = NODE_RADIUS + 20;
    
    const limitedZones = zoneList;
    
    for (let i = 0; i < limitedZones.length; i++) {
      const zoneId = limitedZones[i];
      let attempts = 0;
      let validPosition = false;
      
      while (!validPosition && attempts < maxAttempts) {
        let newPos;
        
        if (attempts < 50) {
          const angle = (i * 2 * Math.PI) / limitedZones.length;
          const radius = Math.min(SVG_WIDTH, SVG_HEIGHT) / 2 - 100;
          newPos = {
            x: SVG_WIDTH / 2 + radius * Math.cos(angle),
            y: SVG_HEIGHT / 2 + radius * Math.sin(angle),
          };
        } else {
          newPos = {
            x: margin + Math.random() * (SVG_WIDTH - 2 * margin),
            y: margin + Math.random() * (SVG_HEIGHT - 2 * margin),
          };
        }
        
        validPosition = true;
        for (const existingZoneId in positions) {
          if (checkOverlap(newPos, positions[existingZoneId])) {
            validPosition = false;
            break;
          }
        }
        
        if (validPosition) {
          positions[zoneId] = newPos;
        }
        
        attempts++;
      }
      
      if (!positions[zoneId]) {
        const angle = (i * 2 * Math.PI) / limitedZones.length;
        const radius = Math.min(SVG_WIDTH, SVG_HEIGHT) / 2 - 100;
        positions[zoneId] = {
          x: SVG_WIDTH / 2 + radius * Math.cos(angle),
          y: SVG_HEIGHT / 2 + radius * Math.sin(angle),
        };
      }
    }
    
    return positions;
  };

  // Tab management functions with normalized saving
  const createNewTab = (name = "New Graph") => {
    // More concise approach
    const localTimestamp = new Date().toLocaleString('sv-SE'); // 'sv-SE' gives YYYY-MM-DD HH:MM:SS format
    name = "New Graph " + localTimestamp;
    const newTab = {
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      name: name,
      data: {
        droppedZones: {}, // Keep this empty for truly blank canvas
        nodePositions: {},
        edgeEndpoints: {},
        edgeCurvatures: {},
        backgroundImage: null,
      }
    };
    
    setTabs(prevTabs => {
      const newTabs = [...prevTabs, newTab];
      try {
        const normalizedTabs = newTabs.map(tab => ({
          ...tab,
          data: normalizeTabData(tab.data)
        }));
        // localStorage.setItem(STORAGE_KEY_TABS, JSON.stringify(normalizedTabs));
      } catch (error) {
        console.error("Error saving new tab:", error);
      }
      return newTabs;
    });
    
    setActiveTabId(newTab.id);
    // Start with no selected nodes for blank canvas
    setSelectedNodes([]);
  };

const [editingName, setEditingName] = useState(false);
const [editingDescription, setEditingDescription] = useState(false);
const [tempName, setTempName] = useState("");
const [tempDescription, setTempDescription] = useState("");

// Update the startEditingName function to check ownership
const startEditingName = () => {
  if (isViewer()) {
    toast.error('You do not have permission to edit this path');
    return;
  }
  
  const currentTab = getCurrentTab();
  if (currentTab) {
    setTempName(currentTab.name);
    setEditingName(true);
  }
};

// Update the startEditingDescription function to check ownership
const startEditingDescription = () => {
  if (isViewer()) {
    toast.error('You do not have permission to edit this path');
    return;
  }
  
  const currentTab = getCurrentTab();
  if (currentTab) {
    setTempDescription(currentTab.description || '');
    setEditingDescription(true);
  }
};

const saveTabDetails = async() => {
  if (activeTabId && (editingName || editingDescription)) {
    // Create the updated tab data first
    const currentTab = tabs.find(tab => tab.id === activeTabId);
    if (!currentTab) return;
    
    const updatedTab = {
      ...currentTab,
      name: editingName ? tempName.trim() || currentTab.name : currentTab.name,
      description: editingDescription ? tempDescription.trim() : currentTab.description
    };

    // Update the tabs state
    setTabs(prevTabs => {
      const newTabs = prevTabs.map(tab =>
        tab.id === activeTabId ? updatedTab : tab
      );
      
      try {
        const normalizedTabs = newTabs.map(tab => ({
          ...tab,
          data: normalizeTabData(tab.data)
        }));
        // localStorage.setItem(STORAGE_KEY_TABS, JSON.stringify(normalizedTabs));
      } catch (error) {
        console.error("Error saving tab details:", error);
      }
      
      return newTabs;
    });

    // Save to cloud with the updated tab
    await saveToCloud(updatedTab);
  }
  
  setEditingName(false);
  setEditingDescription(false);
  setTempName("");
  setTempDescription("");
};

const cancelEditingDetails = () => {
  setEditingName(false);
  setEditingDescription(false);
  setTempName("");
  setTempDescription("");
};

  // Simplified function to load background image using path ID
  const loadBackgroundImageFromS3 = async (pathId) => {
    if (!pathId) return null;
    
    setIsImageLoading(true);
    try {
      const result = await imageService.getImageUrlByPathId(pathId, 7200); // 2 hours
      if (result.success) {
        return result.url;
      }
    } catch (error) {
      console.error('Error loading background image:', error);
      // Don't show error toast here, as the image might just not exist
    } finally {
      setIsImageLoading(false);
    }
    return null;
  };

  const loadSavedGraphs = async () => {
    setShowLoadGraphs(true);
    try {
      let idToken = await getUserSession();
      if (!idToken) {
        toast.error('No authentication token found. Please sign in again.');
        setTimeout(() => {
          navigate('/auth/signin');
        }, 1000);
        return;
      }

      const userInfo = {
        user_email: localStorage.getItem('email'),
        business_name: business_name,
        hw_id: hw_id
      };

      const result = await transitionGraphAPI.getAllAccessibleGraphs(userInfo, idToken);
      if (result.success) {
        
        // Process loaded graphs with proper async handling
        const processedTabs = await Promise.all(
          result.data.map(async (tab) => {
            const denormalizedTab = {
              ...tab,
              data: denormalizeTabData(tab.data)
            };
            
            // If backgroundImageUrl has a value, it means there's an image with this path ID
            if (tab.backgroundImageUrl) {
              denormalizedTab.data.hasBackgroundImage = true;
              try {
                const imageUrl = await loadBackgroundImageFromS3(tab.id);
                denormalizedTab.data.backgroundImageUrl = imageUrl;
              } catch (error) {
                // Set to null if image loading fails
                denormalizedTab.data.backgroundImageUrl = null;
                denormalizedTab.data.hasBackgroundImage = false;
              }
            } else {
              denormalizedTab.data.hasBackgroundImage = false;
              denormalizedTab.data.backgroundImageUrl = null;
            }
            
            return denormalizedTab;
          })
        );
        
        setTabs(processedTabs);
        // Auto-select the first path if none is currently selected
        if (processedTabs.length > 0) {
          const firstTab = processedTabs[0];
          setActiveTabId(firstTab.id);
          setSelectedNodes(Object.keys(firstTab.data.droppedZones || {}));
        } else {
          setActiveTabId(null);
        }
      }
    } catch (error) {
      console.error('Error loading saved graphs:', error);
      toast.error('Failed to load saved graphs');
    }
    setShowLoadGraphs(false);
  };

  // Load saved tabs on component mount with denormalization
  useEffect(() => {
    loadSavedGraphs();
  }, []);

  // Reload graphs and auto-select first path when the site changes
  useEffect(() => {
    setActiveTabId(null);
    setSelectedNodes([]);
    loadSavedGraphs();
  }, [hw_id]); // eslint-disable-line react-hooks/exhaustive-deps


  // Update useEffect to load images when tab changes
  useEffect(() => {
    const loadTabImage = async () => {
      const currentTab = getCurrentTab();
      if (currentTab?.data?.hasBackgroundImage && activeTabId) {
        const imageUrl = await loadBackgroundImageFromS3(activeTabId);
        if (imageUrl) {
          updateCurrentTabData({
            backgroundImageUrl: imageUrl
          });
        } else {
          // Image doesn't exist or failed to load, clear the flag
          updateCurrentTabData({
            hasBackgroundImage: false,
            backgroundImageUrl: null
          });
        }
      }
    };

    if (activeTabId) {
      loadTabImage();
    }
  }, [activeTabId]);

  // Debounced save effect for auto-saving with normalization
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (tabs.length > 0) {
        try {
          const normalizedTabs = tabs.map(tab => ({
            ...tab,
            data: normalizeTabData(tab.data)
          }));
          // localStorage.setItem(STORAGE_KEY_TABS, JSON.stringify(normalizedTabs));
        } catch (error) {
          console.error("Error auto-saving tabs:", error);
        }
      }
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [tabs]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tabDropdownRef && !tabDropdownRef.contains(event.target)) {
        setShowTabDropdown(false);
      }
    };

    if (showTabDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showTabDropdown, tabDropdownRef]);

  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setShowImageUpload(false);
      }
    };

    if (showImageUpload) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showImageUpload]);

  // Function to get color for a zone (using zone ID)
  const getZoneColor = (zoneId) => {
    if (!getCurrentTabData().droppedZones[zoneId]) {
      return defaultColor;
    }
    
    const zoneIndex = zoneId.charCodeAt(zoneId.length - 1) % availableColors.length;
    return availableColors[zoneIndex];
  };

  // Get current position of a node (using zone ID)
  const getCurrentNodePosition = (zoneId, index) => {
    const currentTabData = getCurrentTabData();
    if (currentTabData.droppedZones[zoneId]) return currentTabData.droppedZones[zoneId];
    if (currentTabData.nodePositions[zoneId]) return currentTabData.nodePositions[zoneId];

    const angle = (index * 2 * Math.PI) / zones.length;
    const radius = Math.min(SVG_WIDTH, SVG_HEIGHT) / 2 - 100;
    return {
      x: SVG_WIDTH / 2 + radius * Math.cos(angle),
      y: SVG_HEIGHT / 2 + radius * Math.sin(angle),
    };
  };

  // Get point on circle border closest to target point
  const getPointOnCircleBorder = (center, target, radius) => {
    const dx = target.x - center.x;
    const dy = target.y - center.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance === 0) return center;

    return {
      x: center.x + (dx / distance) * radius,
      y: center.y + (dy / distance) * radius,
    };
  };

  // Convert screen coordinates to SVG coordinates
  const getSVGCoordinates = (e) => {
    if (!svgRef.current) return null;
    const svg = svgRef.current;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    return pt.matrixTransform(svg.getScreenCTM().inverse());
  };

  // Event handlers
  const handleMouseDown = (e, zoneId) => {
    e.preventDefault();
    setDraggedZone(zoneId);
  };

  const handleEdgeEndMouseDown = (e, edge, endType) => {
    e.stopPropagation();
    setDraggedEdgeEnd({ edge, endType });
  };

  const handleEdgeCurveMouseDown = (e, edge) => {
    e.stopPropagation();
    setDraggedEdge({ from: edge.from, to: edge.to });
  };

  const handleMouseMove = (e) => {
    const svgP = getSVGCoordinates(e);
    if (!svgP) return;

    if (isDraggingFromSidebar) {
      setMousePosition(svgP);
    }

    if (draggedZone) {
      const clampedPosition = {
        x: Math.max(NODE_RADIUS, Math.min(svgP.x, SVG_WIDTH - NODE_RADIUS)),
        y: Math.max(NODE_RADIUS, Math.min(svgP.y, SVG_HEIGHT - NODE_RADIUS))
      };
      
      const currentTabData = getCurrentTabData();
      const updateKey = currentTabData.droppedZones[draggedZone] ? 'droppedZones' : 'nodePositions';
      updateCurrentTabData({
        [updateKey]: { ...currentTabData[updateKey], [draggedZone]: clampedPosition }
      });
    }

    if (draggedEdgeEnd) {
      const { edge, endType } = draggedEdgeEnd;
      const edgeKey = `${edge.from}|${edge.to}`;
      
      const clampedPosition = {
        x: Math.max(0, Math.min(svgP.x, SVG_WIDTH)),
        y: Math.max(0, Math.min(svgP.y, SVG_HEIGHT))
      };
      
      const currentTabData = getCurrentTabData();
      updateCurrentTabData({
        edgeEndpoints: {
          ...currentTabData.edgeEndpoints,
          [edgeKey]: { ...currentTabData.edgeEndpoints[edgeKey], [endType]: clampedPosition }
        }
      });
    }

    if (draggedEdge) {
      const { from, to } = draggedEdge;
      const fromPos = getCurrentNodePosition(from, zones.indexOf(from));
      const toPos = getCurrentNodePosition(to, zones.indexOf(to));

      const midX = (fromPos.x + toPos.x) / 2;
      const midY = (fromPos.y + toPos.y) / 2;
      const dx = svgP.x - midX;
      const dy = svgP.y - midY;

      const lineDx = toPos.x - fromPos.x;
      const lineDy = toPos.y - fromPos.y;
      const lineLength = Math.sqrt(lineDx * lineDx + lineDy * lineDy);

      if (lineLength > 0) {
        const perpX = -lineDy / lineLength;
        const perpY = lineDx / lineLength;
        const signedDistance = dx * perpX + dy * perpY;
        const baseCurveScale = Math.min(lineLength * 0.2, 50);
        const curvatureMultiplier = signedDistance / baseCurveScale;

        const currentTabData = getCurrentTabData();
        updateCurrentTabData({
          edgeCurvatures: {
            ...currentTabData.edgeCurvatures,
            [`${from}|${to}`]: curvatureMultiplier
          }
        });
      }
    }
  };

  const handleMouseUp = (event) => {
    if (isDraggingFromSidebar && draggedZone) {
      const svgP = getSVGCoordinates(event);
      if (svgP) {
        const clampedPosition = {
          x: Math.max(NODE_RADIUS, Math.min(svgP.x, SVG_WIDTH - NODE_RADIUS)),
          y: Math.max(NODE_RADIUS, Math.min(svgP.y, SVG_HEIGHT - NODE_RADIUS))
        };
        
        const currentTabData = getCurrentTabData();
        updateCurrentTabData({
          droppedZones: { ...currentTabData.droppedZones, [draggedZone]: clampedPosition }
        });
      }
    }
    setDraggedZone(null);
    setDraggedEdgeEnd(null);
    setDraggedEdge(null);
    setIsDraggingFromSidebar(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (isDraggingFromSidebar && draggedZone) {
      const svgP = getSVGCoordinates(e);
      if (svgP) {
        const clampedPosition = {
          x: Math.max(NODE_RADIUS, Math.min(svgP.x, SVG_WIDTH - NODE_RADIUS)),
          y: Math.max(NODE_RADIUS, Math.min(svgP.y, SVG_HEIGHT - NODE_RADIUS))
        };
        
        const currentTabData = getCurrentTabData();
        updateCurrentTabData({
          droppedZones: {
            ...currentTabData.droppedZones,
            [draggedZone]: clampedPosition
          }
        });
      }
    }
    setIsDraggingFromSidebar(false);
    setDraggedZone(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };

  const handleRemoveNode = (zoneId) => {
    const currentTabData = getCurrentTabData();
    const newDroppedZones = { ...currentTabData.droppedZones };
    delete newDroppedZones[zoneId];

    const newPositions = { ...currentTabData.nodePositions };
    delete newPositions[zoneId];

    const newEndpoints = { ...currentTabData.edgeEndpoints };
    Object.keys(newEndpoints).forEach((key) => {
      if (key.includes(zoneId)) {
        delete newEndpoints[key];
      }
    });

    const newCurvatures = { ...currentTabData.edgeCurvatures };
    Object.keys(newCurvatures).forEach((key) => {
      if (key.includes(zoneId)) {
        delete newCurvatures[key];
      }
    });

    updateCurrentTabData({
      droppedZones: newDroppedZones,
      nodePositions: newPositions,
      edgeEndpoints: newEndpoints,
      edgeCurvatures: newCurvatures
    });

    // Update selectedNodes to remove the deleted zone
    setSelectedNodes(prev => prev.filter(id => id !== zoneId));

    if (hoveredZone === zoneId) {
      setHoveredZone(null);
    }
  };

  // Auto-place all zones with normalized saving - FIX THE SYNC ISSUE
  const autoPlaceZones = () => {
    const autoPositions = generateNonOverlappingPositions(zones);
    updateCurrentTabData({ droppedZones: autoPositions });
    
    // Update selectedNodes to include all zones that are now on the canvas
    setSelectedNodes([...zones]); // This was missing!
    
    setTimeout(() => {
      try {
        const normalizedTabs = tabs.map(tab => 
          tab.id === activeTabId 
            ? { ...tab, data: normalizeTabData({ ...tab.data, droppedZones: autoPositions }) }
            : { ...tab, data: normalizeTabData(tab.data) }
        );
        // localStorage.setItem(STORAGE_KEY_TABS, JSON.stringify(normalizedTabs));
      } catch (error) {
        console.error("Error saving auto-placed zones:", error);
      }
    }, 100);
  };

  // Simplified file upload handler
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file || !file.type.startsWith("image/")) {
      toast.error("Please select a valid image file");
      return;
    }

    if (!activeTabId) {
      toast.error("Please save the path first before adding a background image");
      return;
    }

    setIsImageUploading(true);
    setImageUploadProgress(0);

    try {
      // Upload using path ID as the key
      const currentTabData = getCurrentTabData();
      
      let uploadResult;
      if (currentTabData.hasBackgroundImage) {
        // Update existing image
        uploadResult = await imageService.updateImageByPathId(activeTabId, file);
      } else {
        // This is a bit tricky - we need to upload with a specific key
        // For now, upload normally and then copy to the path ID location
        const tempUpload = await imageService.uploadImage(file, 'transition-graphs', activeTabId);
        
        if (tempUpload.success) {
          // In a real implementation, you'd copy the file to the new location
          // For now, we'll simulate this
          uploadResult = {
            success: true,
            imageKey: `transition-graphs/${activeTabId}`,
            imageUrl: tempUpload.imageUrl
          };
        }
      }
      
      if (uploadResult && uploadResult.success) {
        // Get signed URL for immediate display
        const urlResult = await imageService.getImageUrlByPathId(activeTabId, 7200);
        if (urlResult.success) {
          const url = urlResult.url;
          
          // Create the updated tab data first
          const currentTab = tabs.find(tab => tab.id === activeTabId);
          if (!currentTab) return;
          
          const newTab = {
            ...currentTab,
            data: {
              ...currentTab.data,
              hasBackgroundImage: true,
              backgroundImageUrl: url
            }
          };
          
          // console.log("Updated tab with new background image:", newTab);
          
          // Update the tabs state
          setTabs(prevTabs => 
            prevTabs.map(tab => 
              tab.id === activeTabId ? newTab : tab
            )
          );
          
          // Save to cloud with the new tab
          await saveToCloud(newTab);
          
          toast.success("Background image uploaded successfully");
          setShowImageUpload(false);
        }

      }
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error("Failed to upload image: " + error.message);
    } finally {
      setIsImageUploading(false);
      setImageUploadProgress(0);
    }
  };

  // Simplified drag and drop handler
  const handleImageDrop = async (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (!file || !file.type.startsWith("image/")) {
      toast.error("Please drop a valid image file");
      return;
    }

    if (!activeTabId) {
      toast.error("Please save the path first before adding a background image");
      return;
    }

    // Use the same logic as handleFileUpload
    const fakeEvent = { target: { files: [file] } };
    await handleFileUpload(fakeEvent);
  };

  const handleImageDragOver = (e) => {
    e.preventDefault();
  };

  // Simplified clear current tab data function
  const clearCurrentTabData = () => {
    updateCurrentTabData({
      droppedZones: {},
      nodePositions: {},
      edgeEndpoints: {},
      edgeCurvatures: {},
      hasBackgroundImage: false,
      backgroundImageUrl: null,
    });
  };

  // ADD THESE MISSING STATE VARIABLES
const [imageUploadProgress, setImageUploadProgress] = useState(0);
const [isImageUploading, setIsImageUploading] = useState(false);
const [isImageLoading, setIsImageLoading] = useState(false);

  // Render edge function (updated for zone IDs with | delimiter)
  const renderEdge = (edge, idx) => {
    const fromPos = getCurrentNodePosition(edge.from, zones.indexOf(edge.from));
    const toPos = getCurrentNodePosition(edge.to, zones.indexOf(edge.to));
    const currentTabData = getCurrentTabData();

    if (!currentTabData.droppedZones[edge.from] || !currentTabData.droppedZones[edge.to]) return null;

    const edgeKey = `${edge.from}|${edge.to}`;
    const customEndpoints = currentTabData.edgeEndpoints[edgeKey];

    let startPoint, endPoint;

    if (customEndpoints?.start) {
      startPoint = getPointOnCircleBorder(fromPos, customEndpoints.start, NODE_RADIUS);
    } else {
      startPoint = getPointOnCircleBorder(fromPos, toPos, NODE_RADIUS);
    }

    if (customEndpoints?.end) {
      endPoint = getPointOnCircleBorder(toPos, customEndpoints.end, NODE_RADIUS);
    } else {
      endPoint = getPointOnCircleBorder(toPos, fromPos, NODE_RADIUS);
    }

    const dx = endPoint.x - startPoint.x;
    const dy = endPoint.y - startPoint.y;
    const length = Math.sqrt(dx * dx + dy * dy);

    if (length === 0) return null;

    const midX = (startPoint.x + endPoint.x) / 2;
    const midY = (startPoint.y + endPoint.y) / 2;

    const perpX = -dy / length;
    const perpY = dx / length;
    const baseCurveScale = Math.min(length * 0.2, 50);
    const curvatureMultiplier = currentTabData.edgeCurvatures[edgeKey] || 1;
    const curveScale = baseCurveScale * curvatureMultiplier;

    const controlX = midX + perpX * curveScale;
    const controlY = midY + perpY * curveScale;

    let pathD;
    if (edge.from === edge.to) {
      const offset = NODE_RADIUS * 1.5;
      pathD = `M ${startPoint.x} ${startPoint.y} 
               C ${startPoint.x + offset} ${startPoint.y - offset} 
                 ${endPoint.x + offset} ${endPoint.y - offset} 
                 ${endPoint.x} ${endPoint.y}`;
    } else {
      pathD = `M ${startPoint.x} ${startPoint.y} Q ${controlX} ${controlY} ${endPoint.x} ${endPoint.y}`;
    }

    const t = 0.5;
    const mt = 1 - t;
    const labelX = mt * mt * startPoint.x + 2 * mt * t * controlX + t * t * endPoint.x;
    const labelY = mt * mt * startPoint.y + 2 * mt * t * controlY + t * t * endPoint.y;

    const edgeColor = getZoneColor(edge.from);

    const HANDLE_DISTANCE = 25;
    const startDirection = { x: dx / length, y: dy / length };
    const startHandleX = startPoint.x + startDirection.x * HANDLE_DISTANCE;
    const startHandleY = startPoint.y + startDirection.y * HANDLE_DISTANCE;

    const endDirection = { x: -dx / length, y: -dy / length };
    const endHandleX = endPoint.x + endDirection.x * HANDLE_DISTANCE;
    const endHandleY = endPoint.y + endDirection.y * HANDLE_DISTANCE;

    const viewerMode = isViewer();

    return (
      <g key={`${edge.from}|${edge.to}-${idx}`}>
        <path
          d={pathD}
          fill="none"
          stroke={edgeColor}
          strokeWidth={FIXED_STROKE_WIDTH}
          markerEnd={`url(#arrowhead-${edge.from})`}
          opacity={FIXED_EDGE_OPACITY}
          className="journey-transition-path-animation"
          style={{ cursor: viewerMode ? "default" : "crosshair" }}
          onMouseDown={(e) => !viewerMode && handleEdgeCurveMouseDown(e, edge)}
        />

        {/* Hide edge handles for viewers */}
        {!viewerMode && (
          <>
            <circle
              cx={startHandleX}
              cy={startHandleY}
              r="8"
              fill={edgeColor}
              stroke="white"
              strokeWidth="2"
              opacity="0.8"
              style={{ cursor: "grab" }}
              className="journey-transition-edge-handle"
              onMouseDown={(e) => handleEdgeEndMouseDown(e, edge, "start")}
            />

            <circle
              cx={endHandleX}
              cy={endHandleY}
              r="8"
              fill={edgeColor}
              stroke="white"
              strokeWidth="2"
              opacity="0.8"
              style={{ cursor: "grab" }}
              className="journey-transition-edge-handle"
              onMouseDown={(e) => handleEdgeEndMouseDown(e, edge, "end")}
            />
          </>
        )}

        <rect
          x={labelX - 16}
          y={labelY - 11}
          width="32"
          height="22"
          rx="6"
          fill="rgba(0,0,0,0.75)"
        />
        <text
          x={labelX}
          y={labelY}
          fill={edgeColor}
          fontSize="12"
          fontFamily="monospace"
          fontWeight="bold"
          textAnchor="middle"
          dominantBaseline="central"
          style={{ textShadow: "0 1px 1px rgba(0,0,0,0.5)" }}
        >
          {(edge.probability * 100).toFixed(0)}%
        </text>
      </g>
    );
  };

  // Update render node function to disable interactions for viewers
  const renderNode = (zoneId, index) => {
    const currentTabData = getCurrentTabData();
    if (!currentTabData.droppedZones[zoneId]) return null;

    const pos = getCurrentNodePosition(zoneId, index);
    const zoneName = getZoneDisplayName(zoneId);
    const isHovered = hoveredZone === zoneId;
    const viewerMode = isViewer();

    return (
      <g
        key={zoneId}
        onMouseEnter={() => !viewerMode && setHoveredZone(zoneId)}
        onMouseLeave={() => !viewerMode && setHoveredZone(null)}
      >
        <circle
          cx={pos.x}
          cy={pos.y}
          r={NODE_RADIUS}
          fill={getZoneColor(zoneId)}
          stroke="white"
          strokeWidth="2"
          opacity="1"
          onMouseDown={(e) => !viewerMode && handleMouseDown(e, zoneId)}
          className={viewerMode ? "" : (draggedZone === zoneId ? "journey-transition-node-dragging" : "journey-transition-node-grab")}
        />
        <text
          x={pos.x}
          y={pos.y}
          fill="white"
          fontSize="12"
          fontFamily="monospace"
          fontWeight="bold"
          textAnchor="middle"
          dominantBaseline="central"
          style={{ pointerEvents: "none" }}
        >
          {zoneName}
        </text>

        {/* Hide remove button for viewers */}
        {isHovered && !viewerMode && (
          <g>
            <circle
              cx={pos.x + 25}
              cy={pos.y - 25}
              r="10"
              fill="red"
              stroke="white"
              strokeWidth="2"
              opacity="0.9"
              style={{ cursor: "pointer" }}
              onMouseDown={(e) => {
                e.stopPropagation();
                handleRemoveNode(zoneId);
              }}
            />
            <text
              x={pos.x + 25}
              y={pos.y - 25}
              fill="white"
              fontSize="12"
              fontWeight="bold"
              textAnchor="middle"
              dominantBaseline="central"
              style={{ pointerEvents: "none", userSelect: "none" }}
            >
              ×
            </text>
          </g>
        )}
      </g>
    );
  };

  if (!transitions || Object.keys(transitions).length === 0) {
    return (
      <div className="journey-transition-empty-state">
        No transition data available
      </div>
    );
  }

  const handleMakeGlobal = async () => {
    const currentTab = getCurrentTab();
    if (!currentTab || !currentTab.cloudId) {
      toast.error('Please save the graph to cloud first before making it global');
      return;
    }

    try {
      let idToken = await getUserSession();
      if (!idToken) {
        toast.error('No authentication token found. Please sign in again.');
        setTimeout(() => {
          navigate('/auth/signin');
        }, 1000);
        return;
      }

      const userInfo = {
        user_email: localStorage.getItem('email'),
        business_name: business_name,
        hw_id: hw_id
      };

      const newGlobalStatus = !currentTab.isGlobal;
      
      const result = await transitionGraphAPI.toggleGlobalStatus({
        ...userInfo,
        graphId: currentTab.cloudId,
        isGlobal: newGlobalStatus
      }, idToken);

      if (result.success) {
        // Update the tab with the new global status
        setTabs(prevTabs => 
          prevTabs.map(tab => 
            tab.id === activeTabId 
              ? { ...tab, isGlobal: newGlobalStatus }
              : tab
          )
        );

        toast.success(result.message);
      }
    } catch (error) {
      console.error('Error toggling global status:', error);
      toast.error(`Failed to ${currentTab.isGlobal ? 'remove global status' : 'make graph global'}: ${error.message}`);
    }
  };

  // Simplified remove background image function
  const removeBackgroundImage = async () => {
    if (!activeTabId) return;
    
    try {
      await imageService.deleteImageByPathId(activeTabId);
      toast.success("Background image deleted successfully");
    } catch (error) {
      console.error('Error deleting image from S3:', error);
      // Don't show error if image doesn't exist
      if (!error.message.includes('not found')) {
        toast.error("Failed to delete image from cloud storage");
      }
    }
    //  remove image info from current tab data and tab data
    const currentTab = tabs.find(tab => tab.id === activeTabId);
    if (!currentTab) return;
    
    const newTab = {
      ...currentTab, backgroundImageUrl: null,
      data: {
        ...currentTab.data,
        hasBackgroundImage: false,
        backgroundImageUrl: null
      }
    };
    
    // console.log("Updated tab with new background image:", newTab);
    
    // Update the tabs state
    setTabs(prevTabs => 
      prevTabs.map(tab => 
        tab.id === activeTabId ? newTab : tab
      )
    );
    
    // Save to cloud with the new tab
    await saveToCloud(newTab);

  };

  const handleClearNodes = () => {
    setSelectedNodes([]);
    updateCurrentTabData({ droppedZones: {}, nodePositions: {}, edgeEndpoints: {}, edgeCurvatures: {} });
  };

  const handleResetCanvas = () => {
    setSelectedNodes([]);
    clearCurrentTabData();
  };

  const currentTabData = getCurrentTabData();
  const visibleEdges = hoveredZone
    ? edges.filter((edge) => edge.from === hoveredZone)
    : edges;
  const isViewer = () => {
    const currentTab = getCurrentTab();
    return currentTab && currentTab.isOwnedByUser === false;
  };

  const renderEditMode = () => (
    <>
      <div className="journey-transition-zones-section">
        <SavedPathSelector
          savedPaths={tabs}
          selectedPathId={activeTabId}
          onPathSelect={handlePathSelect}
          isCollapsed={isCollapsed}
          onToggleCollapse={setIsCollapsed}
          handleNewBlank={handleNewBlank}
          showLoadGraphs={showLoadGraphs}
          loadSavedGraphs={loadSavedGraphs}
        />
      </div>

      <div>
        {/* Available Zones - Hide for viewers */}
        {!isViewer() && activeTabId !== null && isCollapsed && (
          <div className="journey-transition-zones-section">
            <div className="journey-transition-zones-header">
              <h3 className="journey-transition-zones-title">Available Zones</h3>
              {/* Auto-Place All Zones Button - Only show if not all zones are placed */}
              {Object.keys(currentTabData.droppedZones).length < zones.length && (
                <button 
                  className="journey-transition-auto-place-button"
                  onClick={autoPlaceZones}
                >
                  <Play size={10} />
                  Auto-Place All
                </button>
              )}
            </div>
            <div className="journey-transition-zones-grid">
              {zones.map(zoneId => {
                const zoneName = getZoneDisplayName(zoneId);
                const isSelected = selectedNodes.includes(zoneId);
                return(
                  <button
                    key={zoneId}
                    className={`journey-transition-zone-button ${isSelected ? 'journey-transition-zone-button-active' : 'journey-transition-zone-button-inactive'}`}
                    onClick={() => {
                      if (isSelected) {
                        // Remove zone from canvas and selectedNodes
                        setSelectedNodes(selectedNodes.filter(id => id !== zoneId));
                        handleRemoveNode(zoneId);
                      } else {
                        // Add zone to canvas and selectedNodes
                        setSelectedNodes([...selectedNodes, zoneId]);
                        
                        // Generate a position for the new zone
                        const currentTabData = getCurrentTabData();
                        const existingPositions = Object.values(currentTabData.droppedZones);
                        let newPosition;
                        
                        // Try to find a non-overlapping position
                        let attempts = 0;
                        const maxAttempts = 50;
                        do {
                          newPosition = {
                            x: NODE_RADIUS + Math.random() * (SVG_WIDTH - 2 * NODE_RADIUS),
                            y: NODE_RADIUS + Math.random() * (SVG_HEIGHT - 2 * NODE_RADIUS),
                          };
                          attempts++;
                        } while (
                          attempts < maxAttempts &&
                          existingPositions.some(pos => checkOverlap(newPosition, pos))
                        );
                        
                        // Add the zone to the canvas
                        updateCurrentTabData({
                          droppedZones: {
                            ...currentTabData.droppedZones,
                            [zoneId]: newPosition
                          }
                        });
                      }
                    }}
                  >
                    <span>
                      {zoneName}
                    </span>
                    {isSelected && (
                      <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: '#ffffff' }}>
                        ✓
                      </span>
                    )}
                  </button>
                )
              })}
            </div>

            {/* Clear Selected Zones Button - Only show if there are zones on canvas */}
            {Object.keys(currentTabData.droppedZones).length > 0 && (
              <button 
                className="journey-transition-clear-button"
                onClick={handleClearNodes}
              >
                <Eraser size={12} />
                Clear All Zones
              </button>
            )}

            {/* Utility Actions - Keep background controls in sidebar */}
            {!isViewer() && getCurrentTab()?.cloudId && (
              <div className="journey-transition-utility-actions">
                {/* Background Controls - Only show if needed */}
                {(currentTabData.hasBackgroundImage || Object.keys(currentTabData.droppedZones).length > 0) && (
                  <div className={`journey-transition-background-controls journey-transition-background-controls-single`}>
                    {/* Set Background Button - Always show */}
                    <button
                      onClick={() => setShowImageUpload(true)}
                      className="journey-transition-background-button journey-transition-button-primary"
                    >
                      <Upload size={12} />
                      {currentTabData.hasBackgroundImage ? 'Reset' : 'Set'} Background
                    </button>
                  </div>
                )}

                {/* Image Upload Modal */}
                {showImageUpload && (
                  <div className="journey-transition-modal-overlay">
                    <div className="journey-transition-modal-content" ref={modalRef}>
                      {/* Close button in top-right corner */}
                      <button
                        onClick={() => setShowImageUpload(false)}
                        className="journey-transition-modal-close-button"
                      >
                        <X size={16} />
                      </button>

                      <h3 className="journey-transition-modal-title">
                        Upload Background Image
                      </h3>

                      <div
                        className="journey-transition-modal-dropzone"
                        onDrop={handleImageDrop}
                        onDragOver={handleImageDragOver}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        {
                          isImageUploading
                          ? <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%'}}><FadeLoader color="white"/></div>
                          : <div>
                              <Upload size={48} className="journey-transition-modal-dropzone-icon" />
                              <p className="journey-transition-modal-dropzone-text">
                                Drop an image here or click to select
                              </p>
                            </div>
                        }
                        
                        <p className="journey-transition-modal-dropzone-subtext">
                          Supports PNG, JPG, GIF, WebP
                        </p>
                      </div>

                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFileUpload}
                        className="journey-transition-hidden"
                      />

                      {
                        currentTabData.backgroundImageUrl && currentTabData.hasBackgroundImage && (
                          <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '16px'}}>
                            <p className="journey-transition-modal-dropzone-subtext">Current Background Image</p>
                            <div className="journey-transition-modal-background-preview-container">
                              <button
                                onClick={removeBackgroundImage}
                                className="journey-transition-modal-close-button"
                                style={{top: '5px', right: '5px', position: 'absolute', zIndex: 10}}
                              >
                                <X size={16} color="#8B0000" />
                              </button>
                              <img
                                src={currentTabData.backgroundImageUrl}
                                alt="Background"
                                className="journey-transition-modal-background-preview"
                                style={{ maxWidth: '100%', maxHeight: '80px', borderRadius: '8px' }}
                              />
                            </div>
                          </div>
                        )
                      }

                    </div>
                  </div>
                )}

              </div>
            )}

          </div>
        )}

        {/* Viewer mode message */}
        {isViewer() && isCollapsed &&(
          <div className="journey-transition-viewer-message">
            <div className="journey-transition-viewer-title">
              View-Only Mode
            </div>
            <div className="journey-transition-viewer-description">
              You can view this path but cannot make changes. Contact the owner for edit access.
            </div>
          </div>
        )}
      </div>
    </>
  );

  // Update the canvas rendering to use the simplified structure
  const renderCanvas = () => {
    if (activeTabId === null) {
      return (
        <div className="journey-transition-empty-state">
          <h3 className="journey-transition-empty-title">Select a Path to View</h3>
          <p style={{
            fontSize: '14px',
            marginBottom: '24px'
          }}>
            Choose a saved path from the sidebar or create a new one to get started
          </p>
        </div>
      );
    }

    const currentTabData = getCurrentTabData();

    // Check if canvas is empty
    if (Object.keys(currentTabData.droppedZones || {}).length === 0 && !currentTabData.hasBackgroundImage) {
      return (
        <div className="journey-transition-empty-canvas">
          <h3 className="journey-transition-empty-title">Empty Canvas</h3>
          <p className="journey-transition-empty-description">
            Add zones individually from the sidebar or use Auto-Generate to quickly place all zones and create your transition path
          </p>
          
          {!isViewer() && (
            <button 
              className="journey-transition-auto-generate-button"
              onClick={autoPlaceZones}
            >
              <Play size={20} />
              <span>Auto-Generate Path</span>
            </button>
          )}
        </div>
      );
    }

    const viewerMode = isViewer();

    return (
      <div className="journey-transition-graph-main">
        <svg
          ref={svgRef}
          width="100%"
          height="700"
          viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
          preserveAspectRatio="xMidYMid meet"
          className="journey-transition-graph-svg"
          onMouseMove={!viewerMode ? handleMouseMove : undefined}
          onMouseUp={!viewerMode ? handleMouseUp : undefined}
          onMouseLeave={!viewerMode ? handleMouseUp : undefined}
          onDrop={!viewerMode ? handleDrop : undefined}
          onDragOver={!viewerMode ? handleDragOver : undefined}
          onDragEnter={!viewerMode ? (e) => e.preventDefault() : undefined}
        >
          {/* Background image with loading state */}
          {currentTabData.backgroundImageUrl && (
            <>
              {isImageLoading && (
                <rect
                  x="0"
                  y="0"
                  width={SVG_WIDTH}
                  height={SVG_HEIGHT}
                  fill="rgba(0,0,0,0.1)"
                />
              )}
              <image
                href={currentTabData.backgroundImageUrl}
                x="0"
                y="0"
                width={SVG_WIDTH}
                height={SVG_HEIGHT}
                opacity={isImageLoading ? "0.3" : "0.7"}
                preserveAspectRatio="xMidYMid meet"
                onLoad={(e) => {
                  // console.log('Image loaded:', e.target);
                  // console.log('Natural dimensions:', e.target.naturalWidth, 'x', e.target.naturalHeight);
                  // console.log('SVG dimensions:', SVG_WIDTH, 'x', SVG_HEIGHT);
                  setIsImageLoading(false);
                }}
                onError={() => {
                  console.error('Failed to load image:', currentTabData.backgroundImageUrl);
                  setIsImageLoading(false);
                  toast.error('Failed to load background image');
                }}
              />
            </>
          )}

          {/* Drag preview indicator */}
          {isDraggingFromSidebar && draggedZone && mousePosition && (
            <circle
              cx={mousePosition.x}
              cy={mousePosition.y}
              r={NODE_RADIUS}
              fill={getZoneColor(draggedZone)}
              stroke="white"
              strokeWidth="2"
              opacity="0.8"
              style={{ pointerEvents: "none" }}
            />
          )}

          <defs>
            {zones.map((zoneId) => (
              <marker
                key={zoneId}
                id={`arrowhead-${zoneId}`}
                viewBox="0 0 10 10"
                refX="9"
                refY="5"
                markerUnits="strokeWidth"
                markerWidth={FIXED_ARROW_SIZE}
                markerHeight={FIXED_ARROW_SIZE}
                orient="auto"
              >
                <path
                  d="M 0 0 L 10 5 L 0 10 z"
                  fill={getZoneColor(zoneId)}
                />
              </marker>
            ))}
          </defs>

          {visibleEdges.map(renderEdge).filter(Boolean)}
          {zones.map(renderNode).filter(Boolean)}
        </svg>
      </div>
    );
  };

  return (
    <div className="journey-transition-container">
      <div className="journey-transition-main-content">
        {/* Contextual Side Panel */}
        {showContextPanel && (
          <div className="journey-transition-side-panel">
            {renderEditMode()}
          </div>
        )}

        {/* Main Canvas Area */}
        <div className="journey-transition-canvas-area">
          {/* Canvas Toolbar */}
          <div className="journey-transition-canvas-toolbar">
            <div className="journey-transition-toolbar-left">
              <button
                onClick={() => setShowContextPanel(!showContextPanel)}
                className="journey-transition-toggle-button"
              >
                {showContextPanel ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
                <div className="journey-transition-tab-details">
                  <div>
                    {activeTabId !== null
                      ? <div>
                          {/* Name Section */}
                          <div className="journey-transition-tab-name-row">
                            <span className="journey-transition-tab-label">Name:</span>
                            {editingName ? (
                              <div className="journey-transition-edit-buttons">
                                <input
                                  maxLength={20}
                                  type="text"
                                  value={tempName}
                                  onChange={(e) => setTempName(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') saveTabDetails();
                                    if (e.key === 'Escape') cancelEditingDetails();
                                  }}
                                  className="journey-transition-edit-input"
                                  autoFocus
                                />
                                <button
                                  onClick={saveTabDetails}
                                  className="journey-transition-edit-save-button"
                                >
                                  ✓
                                </button>
                                <button
                                  onClick={cancelEditingDetails}
                                  className="journey-transition-edit-cancel-button"
                                >
                                  ✕
                                </button>
                              </div>
                            ) : (
                              <div 
                                onClick={!isViewer() ? startEditingName : undefined}
                                className={`${!isViewer() ? 'journey-transition-editable-text' : 'journey-transition-readonly-text'}`}
                                style={{ cursor: isViewer() ? 'default' : 'pointer' }}
                              >
                                {getCurrentTab()?.name}
                                {!isViewer() && (
                                  <span className="journey-transition-edit-hint"> (click to edit)</span>
                                )}
                              </div>
                            )}
                          </div>
                          
                          {/* Description Section */}
                          <div className="journey-transition-tab-description-row">
                            <span className="journey-transition-tab-description-label">Note:</span>
                            {editingDescription ? (
                              <div className="journey-transition-edit-buttons-vertical">
                                <textarea
                                  value={tempDescription}
                                  onChange={(e) => setTempDescription(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' && e.ctrlKey) saveTabDetails();
                                    if (e.key === 'Escape') cancelEditingDetails();
                                  }}
                                  placeholder="Enter description..."
                                  className="journey-transition-edit-textarea"
                                  autoFocus
                                  maxLength={50}
                                />
                                <div className="journey-transition-edit-buttons">
                                  <button
                                    onClick={saveTabDetails}
                                    className="journey-transition-edit-save-button"
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={cancelEditingDetails}
                                    className="journey-transition-edit-cancel-button"
                                  >
                                    Cancel
                                  </button>
                                  <span className="journey-transition-edit-help-text">
                                    Ctrl+Enter to save
                                  </span>
                                </div>
                              </div>
                            ) : (
                              <div 
                                onClick={!isViewer() ? startEditingDescription : undefined}
                                className={`${!isViewer() ? 'journey-transition-editable-description' : 'journey-transition-readonly-description'}`}
                                style={{ cursor: isViewer() ? 'default' : 'pointer' }}
                              >
                                {getCurrentTab()?.description || 'No description provided'}
                                {!isViewer() && !getCurrentTab()?.description && (
                                  <span className="journey-transition-edit-hint"> (click to add)</span>
                                )}
                                {!isViewer() && getCurrentTab()?.description && (
                                  <span className="journey-transition-edit-hint"> (click to edit)</span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      : 'New Blank Path'
                    }
                  </div>
                </div>
            </div>

            {/* Primary Action Buttons - Only show in edit mode and if not viewer */}
            {activeTabId !== null && !isViewer() && (
              <div className="journey-transition-toolbar-right">
                {/* Make Global/Private Button */}
                {activeTabId !== null && getCurrentTab()?.cloudId && (
                  <button 
                    className={`journey-transition-button ${getCurrentTab()?.isGlobal ? 'journey-transition-button-danger' : 'journey-transition-button-purple'}`}
                    onClick={handleMakeGlobal}
                  >
                    <Users size={14} />
                    {getCurrentTab()?.isGlobal ? 'Make Private' : 'Make Public'}
                  </button>
                )}

                {/* Save Button */}
                <button 
                  className="journey-transition-button journey-transition-button-success"
                  onClick={()=>{
                    saveToLocalStorage()
                    saveToCloud()
                  }}
                  disabled={isSaving || isCloudSaving}
                >
                  <Save size={14} />
                  {isSaving || isCloudSaving ? 'Saving...' : 'Save'}
                </button>

                {/* Delete Button */}
                <button 
                  className="journey-transition-button journey-transition-button-danger"
                  onClick={() => {
                    const currentTab = getCurrentTab();
                    if (currentTab && currentTab.cloudId) {
                      // Graph is saved to cloud, delete from cloud
                      deleteGraphFromCloud(currentTab.cloudId);
                    } else {
                      // Graph is only local draft, delete locally
                      deleteLocalDraft();
                    }
                  }}
                >
                  <Trash2 size={14} />
                  {getCurrentTab()?.cloudId ? 'Delete' : 'Delete Draft'}
                </button>
              </div>
            )}
          </div>

          {/* Canvas */}
          <div className="journey-transition-canvas">
            <div className="journey-transition-canvas-content">
              {renderCanvas()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransitionGraph;

const SavedPathSelector = ({ 
  savedPaths = [], 
  selectedPathId, 
  onPathSelect,
  isCollapsed,
  onToggleCollapse,
  handleNewBlank,
  showLoadGraphs,
  loadSavedGraphs
}) => {
  const [pathFilter, setPathFilter] = useState('all');
  const [additionalFilters, setAdditionalFilters] = useState([]);
  const [showFilterPopover, setShowFilterPopover] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const filterPopoverRef = useRef(null);

  // Close filter popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterPopoverRef.current && !filterPopoverRef.current.contains(event.target)) {
        setShowFilterPopover(false);
      }
    };

    if (showFilterPopover) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showFilterPopover]);

  const mainTags = [
    { key: 'owned', label: 'Mine', color: '#2563eb' },
    { key: 'shared', label: 'Shared with me', icon: Eye, color: '#ca8a04' },
    { key: 'unsaved', label: 'Unsaved', icon: Edit3, color: '#ea580c' }
  ];

  const additionalOptions = [
    { key: 'public', label: 'Public Only', icon: Globe },
    { key: 'private', label: 'Private Only', icon: User },
    { key: 'recent', label: 'Recently Modified', icon: null },
    { key: 'has-description', label: 'Has Description', icon: null }
  ];

  const getPathType = (path) => {
    if (!path.cloudId) return 'unsaved';
    if (path.isOwnedByUser && path.isGlobal) return 'public-owned';
    if (path.isOwnedByUser && !path.isGlobal) return 'private-owned';
    if (!path.isOwnedByUser && path.isGlobal) return 'public-shared';
    return 'unknown';
  };

  const getPathTypeLabel = (type) => {
    switch (type) {
      case 'public-owned': return { icon: Globe, color: '#10b981', label: 'Public' };
      case 'private-owned': return { icon: null, color: '#3b82f6', label: 'Private' };
      case 'public-shared': return { icon: Eye, color: '#f59e0b', label: 'Shared' };
      case 'unsaved': return { icon: Edit3, color: '#f97316', label: 'Unsaved' };
      default: return { icon: User, color: '#6b7280', label: 'Unknown' };
    }
  };

  const filterPaths = (paths, mainFilter, additionalFilters, searchTerm) => {
    let filtered = paths;
    
    // Apply search filter
    if (searchTerm.trim()) {
      filtered = filtered.filter(p => 
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (p.description && p.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    // Apply main filter
    switch (mainFilter) {
      case 'owned':
        filtered = filtered.filter(p => p.isOwnedByUser);
        break;
      case 'shared':
        filtered = filtered.filter(p => !p.isOwnedByUser);
        break;
      case 'unsaved':
        filtered = filtered.filter(p => !p.cloudId);
        break;
      default:
        break;
    }

    // Apply additional filters
    additionalFilters.forEach(filter => {
      switch (filter) {
        case 'public':
          filtered = filtered.filter(p => p.isGlobal);
          break;
        case 'private':
          filtered = filtered.filter(p => !p.isGlobal);
          break;
        case 'recent':
          // Could implement date-based filtering here
          break;
        case 'has-description':
          filtered = filtered.filter(p => p.description && p.description.trim() !== '');
          break;
      }
    });

    return filtered;
  };

  const filteredPaths = filterPaths(savedPaths, pathFilter, additionalFilters, searchTerm);
  const getCurrentTab = (selectedPathId) => {
    return savedPaths.find(tab => tab.id === selectedPathId);
  };
  const currentPath = getCurrentTab(selectedPathId);

  const toggleMainFilter = (key) => {
    setPathFilter(pathFilter === key ? 'all' : key);
  };

  const toggleAdditionalFilter = (key) => {
    setAdditionalFilters(prev => 
      prev.includes(key) 
        ? prev.filter(f => f !== key)
        : [...prev, key]
    );
  };

  const clearAllFilters = () => {
    setPathFilter('all');
    setAdditionalFilters([]);
    setSearchTerm('');
  };

  const hasActiveFilters = pathFilter !== 'all' || additionalFilters.length > 0 || searchTerm.trim();

  const handlePathSelect = (path) => {
    onPathSelect(path);
    onToggleCollapse(true);
  };

  const PathDropdownItem = ({ path, isSelected, onClick }) => {
    const pathType = getPathType(path);
    const { icon: Icon, color } = getPathTypeLabel(pathType);
    
    return (
      <div 
        className={`journey-transition-path-item ${isSelected ? 'journey-transition-path-item-selected' : ''}`}
        onClick={() => onClick(path)}
      >
        <div className="journey-transition-path-item-content">
          <div className="journey-transition-path-item-header">
            <span className={`journey-transition-path-item-name ${isSelected ? 'journey-transition-path-item-name-selected' : ''}`}>
              {path.name}
            </span>
            {Icon && <Icon size={10} style={{ color }} className="journey-transition-path-item-icon" />}
            {!path.cloudId && (
              <span className="journey-transition-path-item-unsaved-badge">
                unsaved
              </span>
            )}
            {/* Add selected indicator */}
            {isSelected && (
              <span className="journey-transition-path-item-selected-indicator">
                ✓
              </span>
            )}
          </div>
          <div className="journey-transition-path-item-meta">
            {Object.entries(path.data.droppedZones).length} zones • {path.lastModified}
          </div>
        </div>
      </div>
    );
  };

  const SelectedPathCard = ({ path, onClick }) => {
    const pathType = getPathType(path);
    const { icon: Icon } = getPathTypeLabel(pathType);
    
    return (
      <div 
        className="journey-transition-selected-path-card"
        onClick={onClick}
      >
        <div className="journey-transition-selected-path-card-content">
          <div className="journey-transition-selected-path-card-info">
            <div className="journey-transition-selected-path-card-header">
              <span className="journey-transition-selected-path-card-name">
                {path.name}
              </span>
              {
                Icon && <Icon size={10} className="journey-transition-selected-path-card-icon" />
              }
              {!path.cloudId && (
                <span className="journey-transition-selected-path-card-unsaved">
                  unsaved
                </span>
              )}
            </div>
            <div className="journey-transition-selected-path-card-description">
              {path.description || "No description provided"}
            </div>
            <div className="journey-transition-selected-path-card-meta">
              {Object.entries(path.data.droppedZones).length} zones
            </div>
          </div>
          <ChevronDown size={12} className="journey-transition-selected-path-card-chevron" />
        </div>
      </div>
    );
  };

  return (
    <div className="journey-transition-path-selector">
      {/* Header */}
      <div className="journey-transition-path-header">
        <h2 className="journey-transition-path-title">
          Paths
          {
            showLoadGraphs 
            ? <BeatLoader color="white" size={12} />
            : <div 
            className="journey-transition-refresh-button"
            onClick={loadSavedGraphs}
          >
            <RefreshCcw size={12}/>
          </div>
          }
        </h2>
        <button 
          onClick={() => onToggleCollapse(!isCollapsed)}
          className="journey-transition-path-toggle"
        >
          <ChevronUp 
            size={16} 
            className={`journey-transition-path-toggle-icon ${isCollapsed ? 'journey-transition-path-toggle-icon-collapsed' : ''}`}
          />
        </button>
      </div>
      
      {/* Expandable Content with Animation */}
      <div 
        className={`journey-transition-expandable-content ${isCollapsed ? 'journey-transition-expandable-collapsed' : 'journey-transition-expandable-expanded'}`}
      >
        {/* Filter Section */}
        <div className="journey-transition-filter-section">
          {/* Main Filter Tags with Clear and Filter buttons on the right */}
          <div className="journey-transition-filter-main">
            {/* Tags on the left */}
            <div className="journey-transition-filter-tags">
              {mainTags.map(tag => {
                const isActive = pathFilter === tag.key;
                const Icon = tag?.icon || null;
                return (
                  <button
                    key={tag.key}
                    onClick={() => toggleMainFilter(tag.key)}
                    className={`journey-transition-filter-tag ${
                      isActive 
                        ? `journey-transition-filter-tag-${tag.key}` 
                        : 'journey-transition-filter-tag-inactive'
                    }`}
                    style={isActive ? { backgroundColor: tag.color } : {}}
                  >
                    {
                      Icon && <Icon size={10} style={{ marginRight: '4px' }} />
                    }
                    {tag.label}
                  </button>
                );
              })}
            </div>

            {/* Actions on the right */}
            <div className="journey-transition-filter-actions">
              {/* Clear button */}
              {hasActiveFilters && (
                <button 
                  onClick={clearAllFilters}
                  className="journey-transition-filter-clear"
                >
                  <X size={12} />
                </button>
              )}

              {/* Filter button */}
              <div style={{ position: 'relative', marginRight:"4px" }} ref={filterPopoverRef}>
                <button 
                  onClick={() => setShowFilterPopover(!showFilterPopover)}
                  className={`journey-transition-filter-dropdown-button ${additionalFilters.length > 0 ? 'journey-transition-filter-dropdown-button-active' : ''}`}
                >
                  <Filter size={12} />
                  {additionalFilters.length > 0 && (
                    <span className="journey-transition-filter-indicator"></span>
                  )}
                </button>
                
                {/* Filter dropdown content */}
                {showFilterPopover && (
                  <div className="journey-transition-filter-dropdown">
                    <div className="journey-transition-filter-dropdown-content">
                      <div className="journey-transition-filter-dropdown-title">
                        Additional Filters:
                      </div>
                      <div className="journey-transition-filter-dropdown-options">
                        {additionalOptions.map(option => {
                          const isActive = additionalFilters.includes(option.key);
                          const Icon = option.icon;
                          return (
                            <button
                              key={option.key}
                              onClick={() => toggleAdditionalFilter(option.key)}
                              className={`journey-transition-filter-dropdown-option ${isActive ? 'journey-transition-filter-dropdown-option-active' : ''}`}
                            >
                              {Icon && <Icon size={12} />}
                              {option.label}
                              {isActive && <span className="journey-transition-filter-dropdown-option-check">✓</span>}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Active Additional Filters Display */}
          {additionalFilters.length > 0 && (
            <div className="journey-transition-active-filters">
              {additionalFilters.map(filterKey => {
                const option = additionalOptions.find(opt => opt.key === filterKey);
                return (
                  <span
                    key={filterKey}
                    className="journey-transition-active-filter-chip"
                  >
                    {option?.label}
                    <button
                      onClick={() => toggleAdditionalFilter(filterKey)}
                      className="journey-transition-active-filter-remove"
                    >
                      <X size={10} />
                    </button>
                  </span>
                );
              })}
            </div>
          )}
        </div>

        {/* Search and New Blank in same row */}
        <div className="journey-transition-search-row">
          <div className="journey-transition-search-container">
            {/* Search Input - takes up most of the width */}
            <div className="journey-transition-search-input-container">
              <Search 
                size={12} 
                className="journey-transition-search-icon"
              />
              <input 
                type="text" 
                placeholder="Search..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="journey-transition-search-input"
              />
            </div>

            {/* New Blank Button - compact */}
            <button 
              className="journey-transition-new-blank-button"
              onClick={handleNewBlank}
            >
              <Plus size={12} />
              New Blank
            </button>
          </div>
        </div>

        {/* Dropdown List */}
        <div className="journey-transition-path-list">
          {filteredPaths.map((path) => (
            <PathDropdownItem
              key={path.id}
              path={path}
              isSelected={path.id === selectedPathId}
              onClick={handlePathSelect}
            />
          ))}
          
          {/* Show message if no paths found */}
          {filteredPaths.length === 0 && (
            <div className="journey-transition-path-list-empty">
              {searchTerm.trim() || hasActiveFilters 
                ? 'No paths match your filters'
                : 'No saved paths yet'
              }
            </div>
          )}
        </div>
      </div>

      {/* Collapsed State with Animation */}
      <div 
        className={`journey-transition-collapsed-content ${isCollapsed ? 'journey-transition-collapsed-visible' : 'journey-transition-collapsed-hidden'}`}
      >
        {/* Show Selected Path */}
        {currentPath && (
          <div style={{ padding: '0px' }}>
            <SelectedPathCard 
              path={currentPath} 
              onClick={() => onToggleCollapse(false)}
            />
          </div>
        )}

        {/* Show New Blank */}
        {selectedPathId === 'New Blank' && (
          <div style={{ padding: '8px' }}>
            <div 
              className="journey-transition-selected-path-card"
              style={{ backgroundColor: '#475569' }}
              onClick={() => onToggleCollapse(false)}
            >
              <div className="journey-transition-selected-path-card-content">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{
                    width: '20px',
                    height: '20px',
                    backgroundColor: '#64748b',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Plus size={12} style={{ color: '#d1d5db' }} />
                  </div>
                  <span style={{ fontSize: '10px', fontWeight: 500, color: 'white' }}>New Blank</span>
                </div>
                <ChevronDown size={12} style={{ color: 'white' }} />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};